import { useState, useEffect, useCallback, useRef } from 'react';

export function useOnlineStatus() {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [wasOffline, setWasOffline] = useState(false);

    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            if (!navigator.onLine) return;
            setWasOffline(true);
            setTimeout(() => setWasOffline(false), 5000);
        };
        const handleOffline = () => {
            setIsOnline(false);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    return { isOnline, wasOffline };
}

export function useOfflineQueue() {
    const [pendingCount, setPendingCount] = useState(0);
    const [syncing, setSyncing] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const refreshCount = useCallback(async () => {
        const { getQueueCount } = await import('../lib/offlineDb');
        const count = await getQueueCount();
        setPendingCount(count);
    }, []);

    const syncQueue = useCallback(async () => {
        const { getQueue, removeFromQueue, incrementRetry, addSyncHistoryEntry } = await import('../lib/offlineDb');
        const queue = await getQueue();
        if (queue.length === 0) return;

        setSyncing(true);
        let synced = 0;
        let failed = 0;
        const currentUser = sessionStorage.getItem('labflo_username') || 'unknown';

        for (const entry of queue) {
            try {
                const opts: RequestInit = {
                    method: entry.method,
                    headers: { 'Content-Type': 'application/json', ...entry.headers },
                };
                if (entry.body && entry.method !== 'DELETE') {
                    opts.body = JSON.stringify(entry.body);
                }
                const resp = await fetch(entry.url, opts);
                if (resp.ok || resp.status === 409) {
                    await removeFromQueue(entry.id);
                    await addSyncHistoryEntry({
                        module: entry.module,
                        description: entry.description,
                        method: entry.method,
                        url: entry.url,
                        status: 'synced',
                        retryCount: entry.retries,
                        user: currentUser,
                        queuedAt: entry.createdAt,
                        syncedAt: new Date().toISOString(),
                    });
                    synced++;
                } else if (entry.retries >= 3) {
                    const errText = await resp.text().catch(() => `HTTP ${resp.status}`);
                    await removeFromQueue(entry.id);
                    await addSyncHistoryEntry({
                        module: entry.module,
                        description: entry.description,
                        method: entry.method,
                        url: entry.url,
                        status: 'failed',
                        errorMessage: errText,
                        retryCount: entry.retries,
                        user: currentUser,
                        queuedAt: entry.createdAt,
                        syncedAt: new Date().toISOString(),
                    });
                    failed++;
                } else {
                    await incrementRetry(entry.id);
                    failed++;
                }
            } catch (err: any) {
                if (entry.retries >= 3) {
                    await removeFromQueue(entry.id);
                    await addSyncHistoryEntry({
                        module: entry.module,
                        description: entry.description,
                        method: entry.method,
                        url: entry.url,
                        status: 'failed',
                        errorMessage: err?.message || 'Network error',
                        retryCount: entry.retries,
                        user: currentUser,
                        queuedAt: entry.createdAt,
                        syncedAt: new Date().toISOString(),
                    });
                }
                failed++;
            }
        }

        await refreshCount();
        setSyncing(false);
        return { synced, failed };
    }, [refreshCount]);

    useEffect(() => {
        refreshCount();
        intervalRef.current = setInterval(refreshCount, 10000);
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [refreshCount]);

    // Auto-sync when coming back online
    useEffect(() => {
        const handleOnline = () => {
            if (pendingCount > 0) syncQueue();
        };
        window.addEventListener('online', handleOnline);
        return () => window.removeEventListener('online', handleOnline);
    }, [pendingCount, syncQueue]);

    return { pendingCount, syncing, syncQueue, refreshCount };
}
